import { RoomInformation } from "../Types/api";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DifficultyLevel, DifficultyRatings, ProfileSettings, RoomDetails, RoomState, RoomStates, RoomStatus } from "../Types/Game";

const DEBUG = process.env.REACT_APP_DEBUG === 'true';

export const isLocalhost = Boolean(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const localApiPortFallback = window.location.port === "3000" ? "3001" : "3000";
let localAPI =
  process.env.REACT_APP_ESCAPE_ROOM_API_HOST ||
  "http://localhost:4566/restapis/nhbvdftd1p/dev/_user_request_"
let awsAPI = "https://api.test.escaperoom.gembaadvantagelabs.com/escape-room-team-api";

/**
 * Where to send the user after the Cognito round trip (ERD-124). Sign-in always comes
 * back to redirect_uri (the site root) with ?code=&state= on the URL, so a tab opened
 * on /leaderboard used to land on the room selection page with the code still showing.
 * The page asked for is passed as the OIDC state and restored here. Only same-site
 * paths are accepted, so the state can't be used to redirect somewhere else.
 */
export const signinReturnPath = (state: unknown): string => {
  const returnTo = (state as { returnTo?: unknown } | null | undefined)?.returnTo;
  return typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : "/";
};

export const cognitoAuthConfig = {
  authority: "https://cognito-idp.eu-west-2.amazonaws.com/eu-west-2_cZNpLA9RI",
  client_id: "56umjqv8da08pemvs1584b8b3j",
  redirect_uri: "https://test.escaperoom.gembaadvantagelabs.com",
  response_type: "code",
  scope: "phone openid email",
  // Drop ?code=&state= from the address bar and return to the page that started sign-in.
  // Runs before the router mounts, so the router starts on the restored path.
  onSigninCallback: (user: { state?: unknown } | undefined) => {
    window.history.replaceState({}, document.title, signinReturnPath(user?.state));
  },
};

export const escapeRoomApiHost = isLocalhost ? localAPI : awsAPI;
export const teamManagementEndpoint = "team/add";
export const resultsEndpoint = "team/results";
export const validateRoomCodeEndpoint = "profile/validate";
export const revealRoomCodeEndpoint = "room/reveal-code";
export const defaultProfileName = "default"
export const defaultTimeout = 20000;
export const initialProfileSettings: ProfileSettings = {
  profileName: "Local",
  schoolInput: false,
  escapeRoomTime: 1200,
  singleRoom: false,
  showDifficultyIndicators: true,
  rooms: {
    room1: { active: false, order: 1, status: RoomStatus.Open },
    room2: { active: false, order: 2, status: RoomStatus.Open },
    room3: { active: false, order: 3, status: RoomStatus.Open },
    room4: { active: false, order: 4, status: RoomStatus.Open },
    room5: { active: false, order: 5, status: RoomStatus.Open },
    room6: { active: false, order: 6, status: RoomStatus.Open },
    room7: { active: false, order: 7, status: RoomStatus.Open },
    room8: { active: false, order: 8, status: RoomStatus.Open },
    room9: { active: false, order: 9, status: RoomStatus.Open },
    // room99 is the Experiment Room: a developer/test room for previewing
    // in-development activities. order 99 pins it to the end of every list
    // (reorder arrows only swap adjacent orders, so it can't be moved).
    room99: { active: false, order: 99, status: RoomStatus.Open }
  }
};

const DEFAULT_ROOM_STATE = {
  active: false,
  order: 100,
  status: RoomStatus.Open,
};


export const roomInformation: RoomInformation = {
  room1: { name: "Personal Study", codes: ["YOU", "WISH", "THIS"] },
  room2: { name: "The Laboratory", codes: ["WAS", "A", "CHEAT"] },
  room3: { name: "Surveillance Room", codes: ["WAY", "TO", "FIND"] },
  room4: { name: "Boiler Room", codes: ["OUT", "WHAT", "THE"] },
  room5: { name: "Science Fair", codes: ["DOOR", "CODES", "ARE"] },
  room6: { name: "Quantum Escape", codes: ["NICE", "TRY", "THOUGH"] },
  // Decoys, same convention as rooms 1-6 — the real codes are fetched from
  // the API at reveal-time (see revealRoomCodeEndpoint) instead of living
  // here, so they never ship in the JS bundle. See ERD-53.
  room7: { name: "The Last Login", codes: ["ECHO", "NULL", "SYNC"] },
  room8: { name: "Dorky.AI v19.89", codes: ["CHIP", "GLOW", "WARM"] },
  // Decoys, same convention as rooms 7-8 — the real code is only ever
  // revealed once the crossword finale is solved, fetched from the API at
  // that point (see revealRoomCodeEndpoint) rather than living here.
  room9: { name: "Codebreakers' Study", codes: ["ARCHIVE", "SIGNAL", "VAULT"] },
  // Experiment Room has no keypad — codes are unused (decoys for consistency).
  room99: { name: "Experiment Room", codes: ["TEST", "DEMO", "WIPS"] },
};

// Derived from roomInformation — single source of truth for room names.
export const roomLabels = Object.fromEntries(
  Object.entries(roomInformation).map(([k, v]) => [k, v.name])
) as Record<keyof RoomInformation, string>;

// Labels use <br /> for icon display. Plain names must match roomInformation.
// `difficulty` (1-4, easiest-hardest) is each room's default rating for the
// DifficultyIndicator badge; admins can override it globally for all events
// (Event Management → "Adjust difficulty ratings", see DifficultyRatingsContext).
const roomConfig = [
  { id: "room1", className: "intro-icon1", label: "Personal <br />Study", difficulty: 3 },
  { id: "room2", className: "intro-icon2", label: "The <br />Laboratory", difficulty: 2 },
  { id: "room3", className: "intro-icon3", label: "Surveillance <br />Room", difficulty: 1 },
  { id: "room4", className: "intro-icon4", label: "Boiler <br />Room", difficulty: 1 },
  { id: "room5", className: "intro-icon5", label: "Science <br />Fair", difficulty: 2 },
  { id: "room6", className: "intro-icon6", label: "Quantum <br />Escape", difficulty: 2 },
  { id: "room7", className: "intro-icon7", label: "The Last Login", difficulty: 4 },
  { id: "room8", className: "intro-icon8", label: "Dorky.AI v19.89", difficulty: 3 },
  { id: "room9", className: "intro-icon9", label: "Codebreakers' <br />Study", difficulty: 4 },
  { id: "room99", className: "intro-icon99", label: "Experiment <br />Room", difficulty: 1 },
] as const;

// Derived from roomConfig — each room's built-in difficulty, used whenever the
// global ratings haven't overridden it (see resolveRoomDifficulty).
export const defaultRoomDifficulty: Record<string, DifficultyLevel> = Object.fromEntries(
  roomConfig.map(({ id, difficulty }) => [id, difficulty])
);

// Rooms whose difficulty admins can adjust, in display order. The
// Experiment Room is excluded — it's a developer/test room, not a real puzzle.
export const adjustableDifficultyRoomIds: string[] = roomConfig
  .map(({ id }) => id)
  .filter((id) => id !== "room99");

/** Coerce an unvalidated value (e.g. "3" from DynamoDB) to a DifficultyLevel, or undefined if invalid. */
export const toDifficultyLevel = (value: unknown): DifficultyLevel | undefined => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 4 ? (n as DifficultyLevel) : undefined;
};

/** Keep only valid overrides of adjustable rooms from unvalidated data (API/localStorage). */
export const sanitizeDifficultyRatings = (value: unknown): DifficultyRatings => {
  if (!value || typeof value !== "object") return {};
  const source = value as Record<string, unknown>;
  const out: DifficultyRatings = {};
  adjustableDifficultyRoomIds.forEach((id) => {
    const level = toDifficultyLevel(source[id]);
    if (level !== undefined) out[id] = level;
  });
  return out;
};

/** A room's effective difficulty: the global override if set, else the room's default. */
export const resolveRoomDifficulty = (id: string, ratings?: DifficultyRatings): DifficultyLevel =>
  ratings?.[id] ?? defaultRoomDifficulty[id] ?? 1;

export const roomDetails = (roomStates: RoomStates, ratings?: DifficultyRatings): RoomDetails[] => {

  // eslint-disable-next-line no-console -- intentional dev-only diagnostic, gated behind REACT_APP_DEBUG
  if (DEBUG) console.log("Creating room details from states:", roomStates);

  return roomConfig.map(({ id, className, label }): RoomDetails => {
    const state = roomStates ? roomStates[id] ?? DEFAULT_ROOM_STATE : DEFAULT_ROOM_STATE;

    return {
      id,
      state: state.active,
      order: state.order,
      status: state.status,
      className,
      label,
      difficulty: resolveRoomDifficulty(id, ratings),
    };
  });

};

export const introDefault = (activeRoomCount: number) => {
  return [
    "Test your puzzle solving and observation skills.",
    `${activeRoomCount === 1
      ? "Escape the room within the time left on the clock."
      : "Escape a series of rooms before the time runs out!"
    }`,
    `In this challenge you'll be entering ${activeRoomCount}
        ${activeRoomCount === 1 ? "room" : "rooms"}.`,
  ];
};

export const introSinglePlaythrough = [
  "Test your puzzle solving and observation skills.",
  "Escape the room before the timer runs out!",
];

export const introSinglePlayRooms = [
  "In this challenge, you can decide which room you want to try and solve.",
  "If you manage to escape you can try one of the other rooms!",
  "Start by choosing which room you want to play."
];

export const introCompleted = [
  "Congratulations! All the rooms have been completed!",
];

export const leaderboardRefreshTime = 10000;
